/**
 * Identifies a model-appended citation line, e.g. "[Fonte: …]", "↳ [Manual · p. N]".
 * The `i` flag lets lowercase "[fonte: X]" (occasionally output by older model versions)
 * be treated identically to the canonical casing — both are filtered from the body and
 * extracted to the Fontes footer.
 */
export const CITATION_LINE: RegExp = /^\s*(?:↳\s*)?\[(?:Fonte:)?[^\]]+\]\s*$/i;

/** Convenience wrapper around {@link CITATION_LINE} for use in Array.filter callbacks. */
export function isCitationLine(line: string): boolean {
	return CITATION_LINE.test(line);
}

/**
 * Plain-text conversion for the "copy message" action. Flô replies are chat markdown
 * (bold, headers, bullets, the occasional table) and get pasted into Word / Google Docs / PGR
 * documents, so we strip the markup to clean plain text rather than copy raw `**`/`#`.
 */
export function markdownToPlainText(md: string): string {
	return md
		.replace(/```[a-zA-Z]*\n?([\s\S]*?)```/g, '$1') // fenced code → its content
		.replace(/`([^`]+)`/g, '$1') // inline code
		.replace(/!\[[^\]]*\]\([^)]*\)/g, '') // images → drop
		.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // links → label only
		.replace(/^\s*\|?[\s:|-]{3,}\|?\s*$/gm, '') // table separator rows + horizontal rules
		.replace(/\|/g, ' ') // remaining table pipes → space
		.replace(/^\s{0,3}#{1,6}\s+/gm, '') // ATX headers
		.replace(/\*\*([^*]+)\*\*/g, '$1') // **bold**
		.replace(/__([^_]+)__/g, '$1') // __bold__
		.replace(/\*([^*]+)\*/g, '$1') // *italic*
		.replace(/^\s*>\s?/gm, '') // blockquote markers
		.replace(/^(\s*)[-*+]\s+/gm, '$1• ') // list bullets → •
		.replace(/[ \t]{2,}/g, ' ') // collapse space runs (from pipe removal)
		.replace(/\n{3,}/g, '\n\n') // collapse blank-line runs
		.split('\n')
		.map(l => l.replace(/[ \t]+$/, ''))
		.join('\n')
		.trim();
}

/** Field separator inside a citation line ("title · seção · p. N"). */
const CITATION_SEPARATOR = ' · ';

/**
 * A stored citation line ("[Fonte: …]" / "↳ [title · seção · p. N]") → readable "title · seção · p. N".
 * Drops the "↳", the "[Fonte:" wrapper (any case) and the trailing "]", and collapses an adjacent
 * segment that merely repeats the previous one ("X · X" → "X") — which happens for KB chunks whose
 * section equals their source title. Single source of truth for both the rendered Fontes footer
 * (flo-message `sources`) and the copied text (buildCopyText), so the two never diverge.
 */
export function citationToPlain(line: string): string {
	const stripped = line
		.replace(/^\s*↳\s*/, '')
		.replace(/^\[\s*(?:fonte:\s*)?/i, '')
		.replace(/\s*\]\s*$/, '')
		.trim();
	const segments = stripped.split(CITATION_SEPARATOR);
	return segments
		.filter((seg, i) => i === 0 || seg.trim().toLowerCase() !== segments[i - 1].trim().toLowerCase())
		.join(CITATION_SEPARATOR);
}

/**
 * Builds the clipboard text for a Flô message: the plain-text body, plus a "Fontes:" block when
 * the message carries citations.
 */
export function buildCopyText(body: string, citations: string[]): string {
	const text = markdownToPlainText(body);
	const sources = citations.map(citationToPlain).filter(s => s.length > 0);
	return sources.length ? `${text}\n\nFontes:\n${sources.map(s => `- ${s}`).join('\n')}` : text;
}
